import React, { useMemo, useRef, useEffect } from 'react';
import { Button, Ref } from 'semantic-ui-react';
// @ts-expect-error react-excel-workbook
import Workbook from 'react-excel-workbook';
import moment from 'moment';
import { QuestionDownloadItem } from '../../types';

type QuestionExcelDownloadButtonProps = {
  style?: React.CSSProperties;
  downloadable: boolean;
  onRequestDownloadPress: () => void;
  startDatetime: string;
  endDatetime: string;
  questionExcelData?: QuestionDownloadItem[];
};

const QuestionExcelDownloadButton = ({
  style,
  downloadable,
  onRequestDownloadPress,
  startDatetime,
  endDatetime,
  questionExcelData,
}: QuestionExcelDownloadButtonProps) => {
  const workbookDownloadButtonRef = useRef<HTMLButtonElement>(null);

  const relatedQuestionCountMap: Record<string, number> = useMemo(() => {
    if (!questionExcelData) return {};
    return questionExcelData.reduce((acc: Record<string, number>, question) => {
      const key = getRelatedQuestionCountKey(question);
      if (!acc[key]) acc[key] = 1;
      else acc[key]++;
      return acc;
    }, {});
  }, [questionExcelData]);

  useEffect(() => {
    if (workbookDownloadButtonRef.current && questionExcelData?.length) {
      setTimeout(() => {
        workbookDownloadButtonRef.current?.click();
      }, 100);
    }
  }, [workbookDownloadButtonRef.current, questionExcelData?.length]);

  if (!downloadable || !questionExcelData?.length)
    return (
      <Button
        basic
        fluid
        color="green"
        style={style}
        onClick={onRequestDownloadPress}
      >
        엑셀 다운로드
      </Button>
    );

  return (
    <Workbook
      filename={`문의내용_${moment(startDatetime).format('YYYYMMDD')}-${moment(
        endDatetime,
      ).format('YYYYMMDD')}.xlsx`}
      element={
        <Ref innerRef={workbookDownloadButtonRef}>
          <Button basic fluid color="green" style={style}>
            엑셀 다운로드
          </Button>
        </Ref>
      }
    >
      <Workbook.Sheet data={questionExcelData} name="sheet1">
        <Workbook.Column
          label="문의ID"
          value={(question: QuestionDownloadItem) => question.id}
        />
        <Workbook.Column
          label="유저ID"
          value={(question: QuestionDownloadItem) => question.userId}
        />
        <Workbook.Column
          label="문의시간"
          value={(question: QuestionDownloadItem) =>
            moment(question.createdAt).format('YYYY-MM-DD HH:mm:ss')
          }
        />
        <Workbook.Column
          label="문의내용"
          value={(question: QuestionDownloadItem) => question.content}
        />
        <Workbook.Column
          label="답변내용(기존)"
          value={(question: QuestionDownloadItem) => question.answer ?? ''}
        />
        <Workbook.Column
          label="만족도"
          value={(question: QuestionDownloadItem) =>
            typeof question.isSatisfied === 'boolean'
              ? question.isSatisfied
                ? '만족'
                : '불만족'
              : '-'
          }
        />
        <Workbook.Column
          label="평가 사유"
          value={(question: QuestionDownloadItem) =>
            question.reasons?.length > 0 ? question.reasons.join(', ') : '-'
          }
        />
        <Workbook.Column
          label="추가 의견"
          value={(question: QuestionDownloadItem) =>
            question.detailedReason?.length > 0 ? question.detailedReason : '-'
          }
        />
        <Workbook.Column
          label="답변시간"
          value={(question: QuestionDownloadItem) =>
            moment(question.updatedAt).format('YYYY-MM-DD HH:mm:ss')
          }
        />
        <Workbook.Column
          label="답변소요시간(분)"
          value={(question: QuestionDownloadItem) =>
            question.answer ? durationToMinutes(question.duration) : '-'
          }
        />
        <Workbook.Column
          label="답변자"
          value={(question: QuestionDownloadItem) =>
            question.userAdmin ? question.userAdmin.name : '-'
          }
        />
        <Workbook.Column
          label="카테고리"
          value={(question: QuestionDownloadItem) =>
            question.questionCategory
              ? question.questionCategory.subCategory
              : '-'
          }
        />
        <Workbook.Column
          label="태그"
          value={(question: QuestionDownloadItem) =>
            question.tags.length > 0 ? question.tags.join(', ') : '-'
          }
        />
        <Workbook.Column
          label="챌린지ID"
          value={(question: QuestionDownloadItem) =>
            question.challenge ? question.challenge.id : '챌린지없음'
          }
        />
        <Workbook.Column
          label="연관질문"
          value={(question: QuestionDownloadItem) =>
            relatedQuestionCountMap[getRelatedQuestionCountKey(question)]
          }
        />
        <Workbook.Column
          label="챌린지명"
          value={(question: QuestionDownloadItem) =>
            question.challenge ? question.challenge.title : '-'
          }
        />
        <Workbook.Column
          label="챌린지 시작일"
          value={(question: QuestionDownloadItem) =>
            question.challenge
              ? moment(question.challenge.startDate).format(
                  'YYYY-MM-DD HH:mm:ss',
                )
              : '-'
          }
        />
        <Workbook.Column
          label="챌린지 종료일"
          value={(question: QuestionDownloadItem) =>
            question.challenge
              ? moment(question.challenge.endDate).format('YYYY-MM-DD HH:mm:ss')
              : '-'
          }
        />
        <Workbook.Column
          label="재문의여부"
          value={(question: QuestionDownloadItem) =>
            question.isRetry ? '재문의' : '-'
          }
        />
        <Workbook.Column
          label="삭제여부"
          value={(question: QuestionDownloadItem) =>
            question.isDeleted ? '삭제됨' : '-'
          }
        />
        <Workbook.Column
          label="카테고리ID(개발자용)"
          value={(question: QuestionDownloadItem) =>
            question.questionCategory?.id || 0
          }
        />
        <Workbook.Column
          label="태그(개발자용)"
          value={(question: QuestionDownloadItem) =>
            JSON.stringify(question.tags)
          }
        />
      </Workbook.Sheet>
    </Workbook>
  );
};

const NO_CHALLENGE_TEXT = '(챌린지없음)';
const getRelatedQuestionCountKey = (question: QuestionDownloadItem) => {
  return `${question.userId}${question.data?.id || NO_CHALLENGE_TEXT}`;
};
const durationToMinutes = (duration: number) => duration / (1000 * 60);

export default QuestionExcelDownloadButton;
